import { Component, OnInit, Input, ChangeDetectionStrategy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { ProjectService } from '@app/shell/header/header.project.service';
import { CredentialsService } from '@app/auth';

export interface NavItem {
  label: string;
  route: string;
  routeParams?: any[];
  isActive?: boolean;
}

export interface NavConfig {
  title: string;
  navItems: NavItem[];
}

@Component({
  selector: 'app-analysis-nav',
  templateUrl: './analysis-nav.component.html',
  styleUrls: ['./analysis-nav.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AnalysisNavComponent implements OnInit {
  @Input() analysisType: 'capture' | 'flairr' = 'capture';
  @Input() config!: NavConfig;

  constructor(
    private router: Router,
    public projectService: ProjectService,
    public credentialsService: CredentialsService
  ) {}

  ngOnInit(): void {
    if (!this.config) {
      console.error('AnalysisNavComponent: config is required');
      return;
    }

    this.updateActiveNav();

    // Listen to route changes to update active state
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.updateActiveNav();
    });
  }

  navigateTo(item: NavItem): void {
    if (item.routeParams) {
      this.router.navigate([item.route, ...item.routeParams]);
    } else {
      this.router.navigate([item.route]);
    }
  }

  navigateToHome(): void {
    this.router.navigate(['/home']);
  }

  private updateActiveNav(): void {
    if (!this.config?.navItems) return;

    const currentUrl = decodeURIComponent(this.router.url);

    this.config.navItems.forEach((item) => {
      if (item.routeParams) {
        // For parameterized routes, check if the base route matches and contains the parameter
        const baseRoute = item.route;
        const expectedParam = item.routeParams[0];
        item.isActive =
          currentUrl.startsWith(baseRoute) &&
          (currentUrl.includes(expectedParam) || currentUrl.includes(encodeURIComponent(expectedParam)));
      } else {
        // For simple routes, exact match
        item.isActive = currentUrl === item.route;
      }
    });
  }

  getCompanyName(): string {
    return this.credentialsService.company() || 'No Company';
  }

  getProjectName(): string {
    const selectedProject = this.projectService.selectedProjectDetails();
    return selectedProject?.project_data?.project_name || 'No Project Selected';
  }
}
